/**
 * Pure rules for the Builder product tile and the Featured Product Slider.
 *
 * Both show individually chosen products, so it is a detail read: SCAPI is the
 * system of record (CLAUDE.md rule 1), not CIO.
 */
import type { ShopperProductsTypes } from "@tsf/commerce-sdk-isomorphic";

import type { HecProduct } from "~/hec/types/product";
import { isOutOfStockOverride, isVariantProduct } from "~/hec/types/product";
import { productHitToHecProduct } from "~/hec/utils/product-adapters";
import { buildProductUrl } from "~/hec/utils/product-slug";
import { productsWithSwatches } from "~/shop/utils/products-with-swatches";
import { decodeMasterTilePrice } from "~/shop/utils/scapi-price";

import { FEATURED_PRODUCTS_MAX_IDS } from "./dynamic-product-tile.schemas";
import type {
  FeaturedLookup,
  FeaturedPick,
  FeaturedProductsResult,
} from "./types";

/** The SKU the author typed, trimmed; `null` when blank. */
export function resolveTileProductId(sku: string | undefined): string | null {
  return sku?.trim() || null;
}

/** Trimmed, de-duplicated, non-empty ids in author order. */
export function uniqueProductIds(ids: Array<string | null>): string[] {
  return [...new Set(ids.map(id => id?.trim()).filter(Boolean))] as string[];
}

/**
 * The one batched read for a set of ids. Sorted so the URL — and the route's
 * cache entry — is the same however the author orders the rows; capped at the
 * route's limit so the URL never asks for ids the route would drop.
 */
export function featuredProductsPath(ids: string[]): string {
  const params = new URLSearchParams();
  for (const id of [...ids].slice(0, FEATURED_PRODUCTS_MAX_IDS).sort()) {
    params.append("id", id);
  }
  return `/resources/featured-products?${params.toString()}`;
}

/**
 * SCAPI `Product` → `HecProduct`, priced the way the favorites page prices a
 * master id: `decodeMasterTilePrice` turns a master into the range a shopper
 * can actually buy (COM-205), and a non-master keeps its own `c_priceInfo`.
 * `categoryPath` (attached by the route) rides through for GA4.
 */
export function scapiProductToTileProduct(product: unknown): HecProduct {
  const { decoded } = decodeMasterTilePrice(product);
  return {
    ...productHitToHecProduct(
      productsWithSwatches(product as ShopperProductsTypes.Product),
    ),
    ...(decoded && {
      price: decoded.price,
      c_saleprice: decoded.salePrice,
      priceMax: decoded.priceMax,
    }),
  };
}

/**
 * Per-id outcome of one batched read. An id the batch didn't return is
 * `not-found` (SCAPI omits unknown / offline ids rather than failing the
 * call); a failed batch makes every id `error`.
 */
export function lookupFromBatch(
  ids: string[],
  result: FeaturedProductsResult,
): FeaturedLookup {
  const lookup: FeaturedLookup = {};
  if (result.kind === "error") {
    for (const id of ids) lookup[id] = { kind: "error" };
    return lookup;
  }
  const byId = new Map(result.products.map(p => [p.id, p]));
  for (const id of ids) {
    const product = byId.get(id);
    lookup[id] = product ? { kind: "ok", product } : { kind: "not-found" };
  }
  return lookup;
}

/**
 * Can a shopper buy this right now? False when the storefront's own OOS
 * override says so, or SCAPI reports it unorderable (`inventory.orderable` —
 * for a master that is "no variant orderable").
 */
export function isTileProductInStock(product: HecProduct): boolean {
  if (isOutOfStockOverride(product)) return false;
  if (product.inventory?.orderable === false) return false;
  return product.orderable !== false;
}

/**
 * The first `count` in-stock products in the author's order; `skipped` lists
 * the ids passed over (out of stock / not found / failed). `lookup === null`
 * means the batch is still in flight.
 */
export function pickFeaturedProducts(
  ids: string[],
  lookup: FeaturedLookup | null,
  count: number,
): FeaturedPick {
  if (!lookup) return { products: [], skipped: [], loading: true };
  const products: FeaturedPick["products"] = [];
  const skipped: string[] = [];
  for (const id of ids) {
    if (products.length >= count) break;
    const result = lookup[id];
    if (result?.kind === "ok" && isTileProductInStock(result.product)) {
      products.push({ id, product: result.product });
    } else {
      skipped.push(id);
    }
  }
  return { products, skipped, loading: false };
}

/**
 * Row lookup by SKU where the FIRST row for a SKU wins. A repeated SKU is
 * de-duplicated out of the featured set (`uniqueProductIds`), so its later
 * rows never render — their copy / photo must not overwrite the first row's.
 */
export function firstRowByProductId<T extends { productId: string | null }>(
  rows: T[],
): Map<string, T> {
  const byId = new Map<string, T>();
  for (const row of rows) {
    const id = row.productId?.trim();
    if (id && !byId.has(id)) byId.set(id, row);
  }
  return byId;
}

/** SKUs that appear in more than one row, in first-seen order. */
export function duplicateProductIds(ids: Array<string | null>): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const raw of ids) {
    const id = raw?.trim();
    if (!id) continue;
    if (seen.has(id)) dupes.add(id);
    seen.add(id);
  }
  return [...dupes];
}

/**
 * What the `feature` card needs from a product: display name, PDP link and
 * image. Same fallbacks as `HecProductTileCms` (name → productName, url →
 * built slug, image → image_url) so both layouts link and picture the same.
 */
export function tileCardView(product: HecProduct): {
  name: string;
  href: string;
  image: { link: string; alt: string } | undefined;
} {
  const name = product.name || product.productName || "";
  const link = product.image?.link || product.image_url;
  return {
    name,
    href: product.url || buildProductUrl(product.id, name),
    image: link ? { link, alt: product.image?.alt ?? name } : undefined,
  };
}

/**
 * GA4 / UA / Segment product-click item for the feature card: master
 * `item_id`, variant SKU as `variant`, `category` from the route-attached
 * `categoryPath`. Mirrors `ga4TileItem` in `HecProductTileCms` field for field
 * (SFCC-11026 / SFCC-12141 / SFCC-12175 W6) — that tile is deliberately left
 * untouched, so keep the two in step; `tileClickItem` has a test pinning the
 * shape.
 */
export function tileClickItem(product: HecProduct, price: number | undefined) {
  const p = product as {
    master?: { masterId?: string };
    c_parentMasterProductID?: string;
  };
  return {
    id: p.master?.masterId ?? p.c_parentMasterProductID ?? product.id,
    name: product.name || product.productName || "",
    brand: product.brand,
    variant: isVariantProduct(product) ? product.id : null,
    price,
    category: product.categoryPath ?? "",
  };
}

/**
 * Drop `undefined` values so a spread can't override a default with them —
 * Builder sends `undefined` for a field the author reset.
 */
export function withoutUndefined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, v]) => v !== undefined),
  ) as Partial<T>;
}
